import {
  Button,
  Icon,
  IconName,
  Theme,
  font,
  media,
} from "@literati/public-ui-shared-react";
import { BookShuffleCard } from "components/bookShuffle/BookShuffleCard";
import styled from "styled-components";
import { bookShuffleDeckCardSizeStyles } from "components/bookShuffle/bookShuffleStyles";
import {
  AcceptedDecisionDialog,
  RejectedDecisionDialog,
} from "components/bookShuffle/BookShuffleCardDialogDecision";
import BookShuffleCardStart from "components/bookShuffle/BookShuffleCardStart";
import BookShuffleCardCompleted from "components/bookShuffle/BookShuffleCardCompleted";
import { colors } from "@literati/public-ui-shared-react";
import BookShuffleCardDialogSkip from "components/bookShuffle/BookShuffleCardDialogSkip";
import analytics from "analytics";
import useCardStyles from "components/bookShuffle/useCardStyles";

import { createGlobalStyle } from "styled-components";
import useBookShuffle, {
  BookShuffleProvider,
} from "components/bookShuffle/useBookShuffle";
import { ProviderProps } from "components/bookShuffle/useBookShuffle/types";
import React, { useEffect, useState } from "react";
import ArrowBack from "@/atoms/Icon/arrow-back";

/**
 * Prevent pulling to refresh when swiping down on cards
 * Also prevents swiping left/right for back/forward navigation
 * @see https://developer.chrome.com/blog/overscroll-behavior/
 */
const GlobalStyle = createGlobalStyle`
  html {
    overscroll-behavior: none;
  }
`;

export const SHUFFLE_SUBSCRIPTION_COMPLETION_KEY =
  "book-shuffle-subscription-completion-";
/** This is the className that will be pre-pended to all of BookShuffleDeck's inner component classNames */
export const BOOK_SHUFFLE_DECK_CLASS_BASE = "book-shuffle-deck";

/** These are all of the internal classNames that are used within the BookShuffleDeck component */
export const BookShuffleDeckClassName = {
  BASE: BOOK_SHUFFLE_DECK_CLASS_BASE,
  CARD_WRAPPER: `${BOOK_SHUFFLE_DECK_CLASS_BASE}-card-wrapper`,
  CARD: `${BOOK_SHUFFLE_DECK_CLASS_BASE}-card`,
} as const;

export interface BookShuffleDeckProps extends ProviderProps {
  className?: string;
}

const StyledCardWrapper = styled.div<{
  $i: number;
  $total: number;
}>`
  ${bookShuffleDeckCardSizeStyles}
  border-radius: 20px;
  position: absolute;
  z-index: ${({ $i, $total }) => $total - $i};
`;

const StyledDeck = styled.div`
  position: relative;
  ${bookShuffleDeckCardSizeStyles}
`;

const StyledShuffleControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  ${media.lg} {
    margin-top: 3rem;
  }
`;

const StyledBookCount = styled.p`
  font-family: ${font.fontFamily.avenir};
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 2rem;
  text-align: center;
  align-items: center;
  width: 77px;
`;

const StyledControlButton = styled(Button)<{ $isActive: boolean }>`
  padding: 0.5rem 1rem;
  background: ${colors.white};
  width: auto;
  ${({ $isActive }) =>
    $isActive &&
    `
    background:${colors.tealShade2};
    color: ${colors.white}!important;
    svg path {
      fill: ${colors.white};
    }
    `}
`;

const StyledOwnsBookButton = styled(StyledControlButton)`
  width: 170px;
  svg {
    margin-right: 0.5rem;
  }
`;

const StyledSkipButton = styled(Button)`
  transition: opacity 0.4s;
  color: ${colors.inkBlack};
  &:focus {
    color: ${colors.inkBlack};
  }
  text-decoration: underline;

  margin-top: 2rem;
  ${media.lg} {
    margin-top: 3rem;
  }
`;

const SKIP_THIS_FOR_NOW_BUTTON_LABEL = "Skip this for now";

export const BOOK_SHUFFLE_CARD_INDEX_OFFSET = 1;

export const BookShuffleDeck = (prop: BookShuffleDeckProps) => {
  const { className = "", ...providerProps } = prop;

  const cls = BookShuffleDeckClassName;

  return (
    <BookShuffleProvider {...providerProps}>
      <Theme>
        <GlobalStyle />
        <AcceptedDecisionDialog />
        <RejectedDecisionDialog />
        <BookShuffleCardDialogSkip />

        <StyledDeck className={`${cls.BASE} ${className}`}>
          <StartCard />
          <BookCards />
          <EndCard />
        </StyledDeck>

        <SkipButton />
        <ShuffleControls />
      </Theme>
    </BookShuffleProvider>
  );
};

interface CardProps {
  index: number;
  children: React.ReactNode;
}

const CardWrapper = (props: CardProps) => {
  const { index, children } = props;

  const cls = BookShuffleDeckClassName;
  const { books } = useBookShuffle();
  const styles = useCardStyles(index);

  // NOTE: This is a dumb hack to force the card to re-render
  // after the child element has actually been created, so we can
  // apply the styling to it.
  const [_, setHasRender] = useState(false);
  useEffect(() => {
    setHasRender(true);
  }, []);

  if (!styles) return null;

  return (
    <StyledCardWrapper
      $i={index}
      $total={books.length}
      className={cls.CARD_WRAPPER}
      style={styles.wrapperStyle}
    >
      {children}
    </StyledCardWrapper>
  );
};

const StartCard = () => {
  const index = -1;
  const cls = BookShuffleDeckClassName;

  return (
    <CardWrapper index={index}>
      <BookShuffleCardStart className={cls.CARD} />
    </CardWrapper>
  );
};

const BookCards = () => {
  const { books } = useBookShuffle();
  const cls = BookShuffleDeckClassName;

  return (
    <>
      {books.map((book, i) => (
        <CardWrapper key={book.id} index={i}>
          <BookShuffleCard bookIndex={i} className={cls.CARD} />
        </CardWrapper>
      ))}
    </>
  );
};

const EndCard = () => {
  const { books } = useBookShuffle();
  const cls = BookShuffleDeckClassName;

  return (
    <CardWrapper index={books.length}>
      <BookShuffleCardCompleted className={cls.CARD} />
    </CardWrapper>
  );
};

const SkipButton = () => {
  const { allowSkip, currentIndex, setShowSkipConfirm } = useBookShuffle();

  // prevent the button if it's not allowed in this view
  if (!allowSkip) return null;

  // only show on the landing card
  if (currentIndex !== -1) return null;

  return (
    <StyledSkipButton
      variant="unstyled"
      onClick={() => {
        setShowSkipConfirm(true);
        analytics.trackAmplitudeClickEvent(SKIP_THIS_FOR_NOW_BUTTON_LABEL);
      }}
    >
      {SKIP_THIS_FOR_NOW_BUTTON_LABEL}
    </StyledSkipButton>
  );
};

const OwnsBookButton = () => {
  const { currentIndex, books, toggleOwnsBook } = useBookShuffle();

  const isActive = Boolean(books[currentIndex].ownsBook);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleOwnsBook();
  };

  return (
    <StyledOwnsBookButton $isActive={isActive} onClick={handleClick}>
      {isActive && <Icon name={IconName.CHECKMARK} color="white" />}
      Already Own It
    </StyledOwnsBookButton>
  );
};

const BackButton = () => {
  const { goBack } = useBookShuffle();
  return (
    <StyledControlButton $isActive={false} onClick={goBack}>
      <ArrowBack fill="black" />
    </StyledControlButton>
  );
};

const ShuffleControls = () => {
  const { currentIndex, books } = useBookShuffle();

  // do not show on landing card
  if (currentIndex === -1) return null;
  // do not show on end card
  if (currentIndex >= books.length) return null;

  return (
    <StyledShuffleControls>
      <BackButton />
      <OwnsBookButton />
      <StyledBookCount>
        <strong>{currentIndex + 1}</strong> of <strong>{books?.length}</strong>
      </StyledBookCount>
    </StyledShuffleControls>
  );
};
